import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Descendant } from "slate";
import { ManualState, ManualAction } from "../types";
import { manualReducer, initialState } from "./ManualReducer";
import {
  deserializeFromSlate,
  serializeToSlate,
  createNewChapter,
} from "../utils/serializers";
import { useManual, useManualSave, useManualAutoSave } from "../../hooks";
import { useTranslation } from "react-i18next";
import { dummyContent } from "../../dummycontent";
import { dummyContentEn } from "../../dummycontent.en";
import { dummyContentNl } from "../../dummycontent.nl";
import { dummyContentPl } from "../../dummycontent.pl";
import { dummyContentFr } from "../../dummycontent.fr";
import { dummyContentIt } from "../../dummycontent.it";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";
import { CustomElement } from "../../types";

const langToMap: Record<string, CustomElement[]> = {
  de: dummyContent,
  en: dummyContentEn,
  nl: dummyContentNl,
  pl: dummyContentPl,
  fr: dummyContentFr,
  it: dummyContentIt,
};

interface ManualContextValue {
  state: ManualState;
  dispatch: React.Dispatch<ManualAction>;
  // Convenience methods
  updateChapter: (chapterId: string, content: Descendant[]) => void;
  selectChapter: (chapterId: string | null) => void;
  selectSubChapter: (subChapterId: string | null) => void;
  addChapter: (title: string) => void;
  deleteChapter: (chapterId: string) => void;
  reorderChapters: (newOrder: string[]) => void;
  saveManual: () => void;
}

const ManualContext = createContext<ManualContextValue | null>(null);

interface ManualProviderProps {
  projectId: string;
  children: React.ReactNode;
}

export const ManualProvider: React.FC<ManualProviderProps> = ({
  projectId,
  children,
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(manualReducer, initialState);
  const { manual, isLoading } = useManual(projectId);
  const { storeManual, isLoading: isSaving } = useManualSave(projectId);
  const { autoSaveManual } = useManualAutoSave(projectId);
  const { project } = useProjectDetails(projectId);
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // Initialize from API data or language template
  useEffect(() => {
    if (!isLoading && !isInitializedRef.current && project) {
      // Use existing manual or load template based on project language
      let contentToLoad = manual;

      if (!manual && project.languages && project.languages.length > 0) {
        const projectLang = project.languages[0].code
          .toLowerCase()
          .split("-")[0];

        contentToLoad = langToMap[projectLang] || langToMap["en"]; // Fallback to English
      }

      if (contentToLoad) {
        const normalizedData = deserializeFromSlate(contentToLoad);
        dispatch({
          type: "SET_INITIAL_DATA",
          data: {
            ...normalizedData,
            selectedChapterId: normalizedData.chapterOrder[0] || null,
          },
        });
        isInitializedRef.current = true;
      }
    }
  }, [manual, isLoading, project]);

  // Set loading state
  useEffect(() => {
    dispatch({ type: "SET_LOADING", isLoading: isLoading || isSaving });
  }, [isLoading, isSaving]);

  // Autosave effect - triggers 6 seconds after content changes
  useEffect(() => {
    if (state.hasUnsavedChanges && isInitializedRef.current) {
      // Clear any existing timer
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }

      // Set a new timer to trigger autosave after 6 seconds
      autosaveTimerRef.current = setTimeout(() => {
        const slateContent = serializeToSlate(state);
        autoSaveManual(slateContent);
        // Delay marking as saved to allow checkmark animation to show
        setTimeout(() => {
          dispatch({ type: "MARK_SAVED" });
        }, 500); // Show checkmark for 500ms
        autosaveTimerRef.current = null;
      }, 6000);
    }

    // Clean up the timer when component unmounts or when dependencies change
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [state.hasUnsavedChanges, state, autoSaveManual]);

  // Convenience methods
  const updateChapter = useCallback(
    (chapterId: string, content: Descendant[]) => {
      dispatch({ type: "UPDATE_CHAPTER_CONTENT", chapterId, content });
    },
    [],
  );

  const selectChapter = useCallback((chapterId: string | null) => {
    dispatch({ type: "SELECT_CHAPTER", chapterId });
  }, []);

  const selectSubChapter = useCallback((subChapterId: string | null) => {
    dispatch({ type: "SELECT_SUBCHAPTER", subChapterId });
  }, []);

  const addChapter = useCallback(
    (title: string) => {
      const newChapter = createNewChapter(title, state.chapterOrder.length);
      dispatch({ type: "ADD_CHAPTER", chapter: newChapter });
      // Auto-select the new chapter
      dispatch({ type: "SELECT_CHAPTER", chapterId: newChapter.id });
    },
    [state.chapterOrder.length],
  );

  const deleteChapter = useCallback((chapterId: string) => {
    dispatch({ type: "DELETE_CHAPTER", chapterId });
  }, []);

  const reorderChapters = useCallback((newOrder: string[]) => {
    dispatch({ type: "REORDER_CHAPTERS", newOrder });
  }, []);

  const saveManual = useCallback(() => {
    const slateContent = serializeToSlate(state);
    storeManual(slateContent);
    dispatch({ type: "MARK_SAVED" });
  }, [state, storeManual]);

  const value: ManualContextValue = {
    state,
    dispatch,
    updateChapter,
    selectChapter,
    selectSubChapter,
    addChapter,
    deleteChapter,
    reorderChapters,
    saveManual,
  };

  return (
    <ManualContext.Provider value={value}>{children}</ManualContext.Provider>
  );
};

export const useManualContext = (): ManualContextValue => {
  const context = useContext(ManualContext);
  if (!context) {
    throw new Error("useManualContext must be used within ManualProvider");
  }
  return context;
};
